import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { BankAccountOwnerType, BankAccountType } from "@/api/bank-accounts"
import AddressSelectOrCreate from "@/settings/addresses/address-select-or-create"
import { useFormContext, useWatch } from "react-hook-form"
import { useEffect } from "react"
import { useUser, useUserActions } from "@/api/users"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { InfoCircledIcon } from "@radix-ui/react-icons"
import { Button } from "@/components/ui/button"
import { onChangeRemoveWhiteSpace } from "@/lib/utils"

export default function BankAccountForm() {
  const accountOwnerType = useWatch({ name: "accountOwnerType" })
  const type = useWatch({ name: "type" })
  const { setValue } = useFormContext()
  const { user } = useUser()
  const { startSepaKycVerification } = useUserActions()

  useEffect(() => {
    if (type === BankAccountType.US) {
      setValue("usDetails", { accountNumber: "", routingNumber: "" })
      setValue("euDetails", undefined)
    } else if (type === BankAccountType.EU) {
      setValue("euDetails", { iban: "", bic: "" })
      setValue("usDetails", undefined)
    }
  }, [type, setValue])

  if (!user) return null

  return (
    <div className="flex flex-col gap-4 py-4 flex-grow overflow-scroll p-1">
      <FormField
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Transfer Type</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select bank account type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">ACH</SelectItem>
                  <SelectItem value="EU" disabled={!user.sepaWithdrawal}>
                    SEPA
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            {!user.sepaWithdrawal && (
              <Alert variant="default">
                <InfoCircledIcon className="h-4 w-4" />
                <AlertTitle>SEPA not enabled</AlertTitle>
                <AlertDescription className="flex flex-col gap-2">
                  You'll need an european address to enable SEPA withdrawals
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={startSepaKycVerification}
                  >
                    Enable SEPA withdrawals
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="bankName"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Bank name</FormLabel>
            <FormControl>
              <Input placeholder="Bank name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="accountOwnerType"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Account Owner Type</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger
                  className={field.value ? "" : "text-gray-300"}
                  data-testid="type"
                >
                  <SelectValue placeholder="Select bank account type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value={BankAccountOwnerType.INDIVIDUAL}
                    data-testid="individual"
                  >
                    Individual
                  </SelectItem>
                  <SelectItem
                    value={BankAccountOwnerType.BUSINESS}
                    data-testid="business"
                  >
                    Business
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {accountOwnerType === BankAccountOwnerType.INDIVIDUAL && (
        <div className="flex flex-row gap-4">
          <FormField
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-6/12">
                <FormLabel required>First name</FormLabel>
                <FormControl>
                  <Input placeholder="First name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-6/12">
                <FormLabel required>Last name</FormLabel>
                <FormControl>
                  <Input placeholder="Last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      {accountOwnerType === BankAccountOwnerType.BUSINESS && (
        <FormField
          name="businessName"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Business Name</FormLabel>
              <FormControl>
                <Input placeholder="Business Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <div className="flex flex-col gap-4 rounded-md bg-blue-30 p-4">
        <FormField
          name="userAddressId"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Address</FormLabel>
              <AddressSelectOrCreate
                onChange={field.onChange}
                value={field.value}
              />
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-col gap-4 rounded-md bg-blue-30 p-4">
        <h4 className="text-sm font-semibold">Bank account details</h4>
        {type === BankAccountType.US && (
          <>
            <FormField
              name="usDetails.accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Account number</FormLabel>
                  <FormControl>
                    <Input placeholder="Account number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="usDetails.routingNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Routing number</FormLabel>
                  <FormControl>
                    <Input placeholder="Routing number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
        {type === BankAccountType.EU && (
          <>
            <FormField
              name="euDetails.iban"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>IBAN</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="IBAN"
                      {...field}
                      onChange={onChangeRemoveWhiteSpace(field.onChange)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="euDetails.bic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>BIC</FormLabel>
                  <FormControl>
                    <Input placeholder="BIC" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
      </div>
    </div>
  )
}
